import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * MA-UI-078: System Activation Hook
 *
 * P4-A1: this hook used to collapse EVERY failure into activation state.
 *
 *     catch { setActivation(null); }   ->  isActivated false, status "not_ready"
 *
 * `GET /activation` is guarded by RequireModule("settings"), which WP-8A
 * protected governance constrains to Account Owner/Admin. So every Manager,
 * Sales Agent, Booking Agent and Customer Service Agent received a 403 -- and
 * the catch above turned that 403 into the red "System Not Ready" badge. The
 * alarm fired BECAUSE the caller was not permitted to know, which is the
 * opposite of what it claimed.
 *
 * A request outcome is therefore no longer allowed to fabricate a business
 * state. `requestState` says what happened to the REQUEST; `status` describes
 * ACTIVATION and is null unless a successful authorized response is in hand.
 *
 * Contract notes for existing consumers:
 *   - `isActivated` keeps its meaning and stays FAIL-CLOSED: it is true only
 *     when an authorized response says so. ProfitPathsPage and
 *     ProfitPathBuilder gate publish/approve on it, so "unknown" must continue
 *     to read as "not activated" for them.
 *   - `status` NO LONGER returns "not_ready" for an unauthorized or failed
 *     request. Callers that render on `status` must treat null as "unknown".
 *   - `loading` is unchanged in spirit: true only while a request is pending.
 */

/** What happened to the REQUEST. Never a statement about activation itself. */
export const ACTIVATION_REQUEST_STATE = Object.freeze({
  /** Not requested -- caller is unauthorized, or `enabled` is false. */
  IDLE: "idle",
  /** In flight. */
  LOADING: "loading",
  /** Authorized response in hand. Only here is `status` meaningful. */
  READY: "ready",
  /** Refused (403). The caller may not know; this is NOT "not ready". */
  UNAUTHORIZED: "unauthorized",
  /** Network failure or 5xx. Also NOT "not ready". */
  ERROR: "error",
});

/**
 * @param {{enabled?: boolean}} [options]
 *   `enabled: false` suppresses the request entirely, so an unauthorized
 *   caller never issues GET /activation at all. Defaults to true, which keeps
 *   every existing call site byte-compatible.
 */
export default function useActivation(options = {}) {
  const { enabled = true } = options;

  // Written from async callbacks after an await, plus one render-time reset
  // below -- never synchronously inside an effect body.
  const [result, setResult] = useState({
    state: enabled
      ? ACTIVATION_REQUEST_STATE.LOADING
      : ACTIVATION_REQUEST_STATE.IDLE,
    data: null,
  });

  // React's documented "adjust state when a prop changes" pattern, during
  // render. This is what stops the PREVIOUS authorization context's answer
  // being reused: when `enabled` flips (an Account switch drives it false then
  // true again, because isResolvedForSelectedAccount necessarily goes false in
  // between), the stored result is discarded rather than carried over. An
  // epoch field was tried first and was NOT sufficient -- a boolean cannot
  // distinguish "enabled for Account A" from "enabled for Account B", and the
  // stale-generation test below proves it.
  const [prevEnabled, setPrevEnabled] = useState(enabled);
  if (prevEnabled !== enabled) {
    setPrevEnabled(enabled);
    setResult({
      state: enabled
        ? ACTIVATION_REQUEST_STATE.LOADING
        : ACTIVATION_REQUEST_STATE.IDLE,
      data: null,
    });
  }

  useEffect(() => {
    // Unauthorized callers never reach the network at all.
    if (!enabled) return undefined;

    // Canonical cancellation: the cleanup runs before the next effect and on
    // unmount, so a response that arrives after the authorization context
    // changed can never overwrite the current one.
    let cancelled = false;

    (async () => {
      try {
        const res = await api.get("/activation");
        if (cancelled) return;
        setResult({ state: ACTIVATION_REQUEST_STATE.READY, data: res.data });
      } catch (err) {
        if (cancelled) return;
        // Only the status code is inspected; no response body is surfaced.
        const code = err?.response?.status;
        setResult({
          state:
            code === 401 || code === 403
              ? ACTIVATION_REQUEST_STATE.UNAUTHORIZED
              : ACTIVATION_REQUEST_STATE.ERROR,
          data: null,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await api.get("/activation");
      setResult({ state: ACTIVATION_REQUEST_STATE.READY, data: res.data });
    } catch (err) {
      const code = err?.response?.status;
      setResult({
        state:
          code === 401 || code === 403
            ? ACTIVATION_REQUEST_STATE.UNAUTHORIZED
            : ACTIVATION_REQUEST_STATE.ERROR,
        data: null,
      });
    }
  }, [enabled]);

  // A disabled caller is IDLE with no data, whatever is stored.
  const requestState = enabled ? result.state : ACTIVATION_REQUEST_STATE.IDLE;
  const activation = enabled ? result.data : null;

  const isReady = requestState === ACTIVATION_REQUEST_STATE.READY;
  const isAuthorized = requestState !== ACTIVATION_REQUEST_STATE.UNAUTHORIZED;
  const loading = requestState === ACTIVATION_REQUEST_STATE.LOADING;

  // Fail-closed, and unchanged for existing consumers: unknown reads as false.
  const isActivated = isReady ? activation?.is_activated ?? false : false;

  // Computed ONLY from an authorized response. Null means "unknown", which is
  // what stops a refusal from rendering as an activation problem.
  let status = null;
  if (isReady) {
    const capabilities = activation?.capabilities || {};
    let configuredCount = 0;
    for (const [, cap] of Object.entries(capabilities)) {
      if (cap?.configured) {
        configuredCount++;
      } else if (cap?.data?.enabled === false) {
        configuredCount++; // intentionally disabled = satisfied
      }
    }
    if (isActivated) status = "activated";
    else if (configuredCount > 0) status = "incomplete";
    else status = "not_ready";
  }

  const updateCapability = useCallback(
    async (capability, configured, data = {}, currentStep = null) => {
      try {
        const payload = { capability, configured, data };
        if (currentStep !== null) payload.current_step = currentStep;
        const res = await api.put("/activation", payload);
        setResult({ state: ACTIVATION_REQUEST_STATE.READY, data: res.data });
        return res.data;
      } catch (err) {
        console.error("[useActivation] updateCapability failed");
        throw err;
      }
    },
    []
  );

  return {
    activation,
    isActivated,
    status,
    loading,
    requestState,
    isAuthorized,
    refresh,
    updateCapability,
  };
}
